import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Card, Typography, IconButton, TextField, Dialog, DialogContent, DialogActions, DialogTitle, Button, Tooltip,
    Grid, Avatar, MenuItem, Checkbox, Chip, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from 'react-router-dom';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import { DataGrid } from "@mui/x-data-grid";
import { createDoctor, deleteDoctors, getDoctors, updateDoctor } from '../services/doctors';

const Doctors = () => {
    const navigate = useNavigate();
    const accountId = localStorage.getItem("account_id");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [openDialog, setOpenDialog] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [doctors, setDoctors] = useState([]);

    const emptyForm = {
        name: "",
        specialization: "",
        status: "active",
        accountId: Number(accountId), // you can make this dynamic later
    };

    const [formData, setFormData] = useState(emptyForm);

    const fetchDoctors = async () => {
        try {
            const data = await getDoctors(accountId);
            setDoctors(data);
        } catch (err) {
            console.error("Failed to load doctors:", err);
        }
    };

    useEffect(() => {
        fetchDoctors();
    }, [accountId]);

    const columns = [
        {
            field: "name",
            headerName: "Doctor",
            flex: 1,
            renderCell: (params) => {
                const name = params.row?.name;
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            height: "100%",
                            minWidth: 0,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 30,
                                height: 30,
                                bgcolor: "primary.main",
                                mr: 1.5,
                                color: "white",
                                fontSize: "1rem",
                                fontWeight: 500,
                            }}
                        >
                            {name
                                ?.split(" ")
                                .map((n) => n[0])
                                .join("")
                                .toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: "#111827",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: "specialization",
            headerName: "Specialization",
            flex: 2,
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        color: "#4b5563",
                        fontWeight: 400,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {params.row.specialization}
                </Typography>
            ),
        },
    ];

    // Handle field change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleRowClick = (row) => {
        setSelectedTemplate(row);
        setFormData({
            name: row.name || "",
            specialization: row.specialization || "",
            status: row.status || "active",
            accountId: row.accountId || accountId,
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setTimeout(() => {
            setSelectedTemplate(null);
            setFormData(emptyForm); // reset everything
        }, 300); // 300ms matches MUI's default transition duration
    };

    const handleSubmit = async () => {
        try {
            if (selectedTemplate) {
                // 🔍 Prepare only changed fields for PATCH
                const updatedFields = {};
                Object.keys(formData).forEach((key) => {
                    if (formData[key] !== selectedTemplate[key]) {
                        updatedFields[key] = formData[key];
                    }
                });

                // ✅ Only send updated fields
                await updateDoctor(selectedTemplate.id, updatedFields, accountId);
                alert("Doctor updated successfully!");
            } else {
                // Create new doctor
                await createDoctor(formData);
                alert("Doctor created successfully!");
            }
            fetchDoctors();
            setOpenDialog(false);
            setFormData(emptyForm);
        } catch (error) {
            console.error(error);
            alert(error.message || "Operation failed");
        }
    };

    const handleDeleteSelected = async () => {
        if (!selectedIds.length) {
            alert("No doctor selected");
            return;
        }

        if (!window.confirm("Are you sure you want to delete the selected doctor(s)?")) return;

        try {
            await deleteDoctors(selectedIds, accountId);
            fetchDoctors();
            setSelectedIds([]);
            alert("Selected doctor(s) deleted successfully");
        } catch (err) {
            console.error("❌ Error deleting doctors:", err);

            // ✅ Axios error handling
            if (err.response?.status === 409) {
                const blockedIds = err.response.data?.blocked_doctor_ids || [];
                alert(
                    `Cannot delete doctor(s).\n\nThey are assigned to patients.\n\nDoctor IDs: ${blockedIds.join(", ")}`
                );
            } else {
                alert(
                    err.response?.data?.error ||
                    err.message ||
                    "Cannot delete doctor(s). They are assigned to patients."
                );
            }
        }
    };

    const toggleSelectDoctor = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
        );
    };

    return (

        <Box sx={{ overflowX: 'hidden', p: { xs: 2, sm: 3 } }}>

            <Box
                display="flex"
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                width="100%"
                gap={{ xs: 1.5, sm: 0 }}
                mb={0}
            >
                <Box>
                    <Typography
                        variant="h6"
                        fontWeight={400}
                        sx={{ color: "#1f2937", mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                    >
                        Registered Doctors
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        mt: { xs: 0, sm: 0 },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'space-between', sm: 'flex-end' }
                    }}
                >
                    <Button
                        variant="contained"
                        startIcon={<AddIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                        onClick={() => {
                            setOpenDialog(true);
                            setSelectedTemplate(false);
                        }
                        }
                        sx={{
                            bgcolor: "#4A90E2",
                            "&:hover": { bgcolor: "#357ABD" },
                            borderRadius: { xs: 3, sm: 2 },
                            px: { xs: 1.5, md: 2.5 },
                            py: { xs: 0.8, sm: 1 },
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            boxShadow: { xs: '0 4px 12px rgba(74, 144, 226, 0.3)', sm: '0 1px 3px rgba(0, 0, 0, 0.1)' },
                            whiteSpace: "nowrap",
                            minWidth: { xs: 'auto', sm: 'auto' },
                            height: { xs: 36, sm: 'auto' },
                            flex: { xs: '1 1 auto', sm: '0 0 auto' },
                        }}
                    >
                        Add Doctor
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<DeleteIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                        onClick={handleDeleteSelected}
                        sx={{
                            borderColor: '#f44336',
                            color: '#f44336',
                            borderRadius: { xs: 3, sm: 2 },
                            px: { xs: 1.5, md: 2.5 },
                            py: { xs: 0.8, sm: 1 },
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            height: { xs: 36, sm: 'auto' },
                            flex: { xs: '1 1 auto', sm: '0 0 auto' },
                            '&:hover': {
                                borderColor: '#d32f2f',
                                bgcolor: 'rgba(244, 67, 54, 0.04)'
                            }
                        }}
                    >
                        Delete Doctors
                    </Button>

                    <Tooltip title="Patients" arrow>
                        <IconButton
                            onClick={() => navigate('/patients')}
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#666',
                                borderRadius: 2,
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                                flex: { xs: '0 0 auto', sm: '0 0 auto' },
                                '&:hover': {
                                    bgcolor: '#e0e0e0',
                                    color: '#333'
                                }
                            }}
                        >
                            <PersonalInjuryIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ py: 2 }}>
                {isMobile ? (
                    /* ---------- Mobile Card View ---------- */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {doctors.length === 0 ? (
                            <Typography sx={{ textAlign: 'center', color: '#6b7280', py: 4 }}>
                                No doctors found
                            </Typography>
                        ) : (
                            doctors.map((doctor) => {
                                const isSelected = selectedIds.includes(doctor.id);
                                return (
                                    <Card
                                        key={doctor.id}
                                        sx={{
                                            borderRadius: 3,
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                                            border: '1px solid #e5e7eb',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                p: 1.5,
                                            }}
                                        >
                                            <Checkbox
                                                checked={isSelected}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    toggleSelectDoctor(doctor.id);
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                                sx={{ p: 0.5 }}
                                            />

                                            <Box
                                                onClick={() => handleRowClick(doctor)}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    flex: 1,
                                                    minWidth: 0,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Avatar
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: 'primary.main',
                                                        fontSize: '1rem',
                                                        fontWeight: 500,
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    {doctor.name
                                                        ?.split(" ")
                                                        .map((n) => n[0])
                                                        .join("")
                                                        .toUpperCase()}
                                                </Avatar>

                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#111827',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {doctor.name}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#4b5563',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {doctor.specialization || '—'}
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    label={doctor.status === 'inactive' ? 'Inactive' : 'Active'}
                                                    size="small"
                                                    sx={{
                                                        flexShrink: 0,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        textTransform: 'capitalize',
                                                        bgcolor: doctor.status === 'inactive' ? '#f3f4f6' : '#dcfce7',
                                                        color: doctor.status === 'inactive' ? '#6b7280' : '#166534',
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Card>
                                );
                            })
                        )}
                    </Box>
                ) : (
                    /* ---------- Desktop / Tablet DataGrid View ---------- */
                    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.05)', border: '1px solid #e5e7eb' }}>

                        <Box sx={{
                            width: '100%',
                            overflowX: 'auto',
                            '&::-webkit-scrollbar': {
                                height: '8px',
                                display: 'block',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: '#f1f1f1',
                                borderRadius: '4px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: '#4a90e2',
                                borderRadius: '4px',
                                border: '1px solid #f1f1f1',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                background: '#357ABD',
                            }
                        }}>
                            <DataGrid
                                rows={doctors}
                                columns={columns}
                                getRowId={(row) => row.id}
                                pageSize={10}
                                rowsPerPageOptions={[10, 20, 50]}
                                checkboxSelection
                                disableRowSelectionOnClick
                                autoHeight
                                getRowHeight={() => 'auto'}
                                selectionModel={selectedIds} // controlled selection
                                onRowSelectionModelChange={(selectionModel) => {
                                    // ✅ Convert Set of IDs to an array (like your customers table)
                                    const idsArray = Array.from(selectionModel.ids);
                                    console.log("✅ Selected doctor IDs:", idsArray);
                                    setSelectedIds(idsArray);
                                }}
                                onRowClick={(params) => handleRowClick(params.row)}
                                sx={{
                                    border: 'none',
                                    borderRadius: 2,
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    minWidth: '700px',
                                    maxWidth: '1800px',
                                    width: '100%',
                                    '& .clickable-row': {
                                        cursor: 'pointer',
                                    },
                                    '& .MuiDataGrid-root': {
                                        border: 'none',
                                    },
                                    '@media (min-width: 769px) and (max-width: 1024px)': {
                                        '& .MuiDataGrid-columnHeader[data-field="reference"]': {
                                            display: 'none',
                                        },
                                        '& .MuiDataGrid-cell[data-field="reference"]': {
                                            display: 'none',
                                        },
                                    },
                                    '& .MuiDataGrid-columnHeaders': {
                                        minHeight: '48px !important',
                                        maxHeight: '48px !important',
                                    },
                                    '& .MuiDataGrid-cell': {
                                        fontSize: '0.95rem',
                                        paddingTop: '12px',
                                        paddingBottom: '12px',
                                        borderRight: 'none !important',
                                        '&:focus': {
                                            outline: 'none !important',
                                            border: 'none !important',
                                        },
                                        '&:focus-within': {
                                            outline: 'none !important',
                                            border: 'none !important',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'transparent !important',
                                            border: 'none !important',
                                        },
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontSize: '0.8rem',
                                        whiteSpace: 'normal',
                                        lineHeight: '1.2',
                                        textAlign: 'left',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        backgroundColor: '#4a90e2',
                                        color: '#ffffff',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        '&:focus, &:focus-within': {
                                            outline: 'none',
                                        },
                                        padding: '0 8px',
                                    },
                                    '& .MuiDataGrid-columnSeparator': {
                                        display: 'none',
                                    },
                                    '& .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIcon': {
                                        color: '#ffffff !important',
                                    },
                                    '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                                        color: '#ffffff !important',
                                    },
                                    '& .MuiDataGrid-columnHeader .MuiIconButton': {
                                        color: '#ffffff !important',
                                        '& svg': {
                                            color: '#ffffff !important',
                                            fill: '#ffffff !important',
                                        },
                                    },
                                    '& .MuiDataGrid-columnHeaderTitleContainer .MuiIconButton': {
                                        color: '#ffffff !important',
                                        '& svg': {
                                            color: '#ffffff !important',
                                            fill: '#ffffff !important',
                                        },
                                    },
                                    '& .MuiDataGrid-columnHeaders .MuiIconButton svg': {
                                        color: '#ffffff !important',
                                        fill: '#ffffff !important',
                                    },
                                    '& .MuiDataGrid-columnHeaders .MuiSvgIcon-root': {
                                        color: '#ffffff !important',
                                        fill: '#ffffff !important',
                                    },
                                    // White checkboxes in header and rows, smaller size
                                    '& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root': {
                                        color: '#ffffff !important',
                                        transform: 'scale(0.8)',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1.2rem',
                                        },
                                        '&.Mui-checked': {
                                            color: '#ffffff !important',
                                        },
                                    },
                                    '& .MuiDataGrid-cellCheckbox .MuiCheckbox-root': {
                                        transform: 'scale(0.8)',
                                        '& .MuiSvgIcon-root': {
                                            fontSize: '1.2rem',
                                        },
                                    },
                                    '& .MuiDataGrid-cell--withRenderer': {
                                        border: 'none !important',
                                        outline: 'none !important',
                                    },
                                    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
                                        border: 'none !important',
                                        outline: 'none !important',
                                    },
                                    '& .MuiDataGrid-row': {
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgba(74, 144, 226, 0.05)',
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: 'rgba(74, 144, 226, 0.1)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(74, 144, 226, 0.15)',
                                            },
                                        },
                                    },
                                }}
                            />

                        </Box>
                    </Card>
                )}
            </Box>

            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: { xs: 0, sm: 2 },
                    }
                }}
            >
                <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                    {selectedTemplate ? 'Edit Doctor' : 'Create Doctor'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={{ xs: 2, sm: 3 }}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Doctor Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Speciality"
                                    name="specialization"
                                    value={formData.specialization}
                                    onChange={handleChange}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>

                            {selectedTemplate && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Status"
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        InputLabelProps={{ shrink: true }}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </TextField>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: { xs: 2, sm: 1.5 },
                        gap: { xs: 1, sm: 0 },
                        flexDirection: { xs: 'column-reverse', sm: 'row' },
                        '& > button': { width: { xs: '100%', sm: 'auto' } },
                    }}
                >
                    <Button onClick={handleCloseDialog}
                        sx={{
                            px: 3, py: 1, borderRadius: '6px', textTransform: 'none', fontWeight: 600,
                            color: '#64748b', border: '1px solid #e0e0e0',
                            minHeight: { xs: '44px', sm: 'auto' },
                            '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                    >Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                            px: 3, py: 1,
                            minHeight: { xs: '44px', sm: 'auto' },
                        }}
                    >
                        {selectedTemplate ? 'Update' : 'Create'} Doctor
                    </Button>
                </DialogActions>
            </Dialog>

            <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .animate-spin {
          animation: spin 1s linear infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: .5; }
        }
      `}</style>
        </Box>
    );
};

export default Doctors;